import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Box, HStack, Kbd, Text, useDisclosure } from '@chakra-ui/react';
import CommandSearchModal, { takeParkedOrgSwitchSearch } from '../modals/CommandSearchModal';
import BulkActionsShortcutMenu from './BulkActionsShortcutMenu';
import SessionContext from '../contexts/SessionContext';
import { useBulkActionsShortcut } from '../contexts/BulkActionsShortcutContext';
import { canUseAskCarl } from '../utils/askCarlAccess';
import { Button, Icon, IconButton, Tooltip } from '../ui';

/**
 * Width of the centered search bar, relative to the TopBar. Exported so the
 * TopBar can cap its flanking clusters and keep them clear of the bar.
 */
export const TOPBAR_SEARCH_WIDTH = 'min(28.125rem, 34%)';

/** Breakpoint at which the full bar replaces the compact icon trigger. */
export const TOPBAR_SEARCH_BAR_BREAKPOINT = 'lg';

/**
 * Global search trigger for the TopBar.
 *
 * Laptop and up: a full input-styled bar. Parent sidebar-tab views (no
 * breadcrumb trail) center it on the viewport (`50vw`, minus the rail).
 * Detail pages with breadcrumbs keep it centered on the TopBar so a long
 * trail can cap against it. `index.css` re-centers it over the visible area
 * when a side drawer is open. Below laptop: a magnifying-glass icon in the
 * right-hand cluster.
 *
 * Either trigger opens the command palette (CommandSearchModal) — same as
 * pressing Cmd/Ctrl+K — where the platform search and Ask Carl live. When a
 * list page has registered bulk actions (rows selected), Cmd/Ctrl+K opens that
 * dropdown instead.
 */
const SearchBar = () => {
    const sessionContext = useContext(SessionContext);
    const palette = useDisclosure();
    const [initialQuery, setInitialQuery] = useState<string | undefined>();
    const askCarlAvailable = canUseAskCarl(sessionContext);

    // An org switch from the palette swaps the session, which may also remount this bar.
    // Whichever instance sees the new session takes the parked query: a still-open palette
    // already has it, a fresh one reopens with it.
    useEffect(() => {
        const parked = takeParkedOrgSwitchSearch();
        if (parked === null || palette.isOpen) return;
        setInitialQuery(parked);
        palette.onOpen();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sessionContext?.sessionId]);

    // Clear only when the palette closes; on a fresh mount this would wipe the query just restored above.
    const wasPaletteOpen = useRef(palette.isOpen);
    useEffect(() => {
        if (wasPaletteOpen.current && !palette.isOpen) setInitialQuery(undefined);
        wasPaletteOpen.current = palette.isOpen;
    }, [palette.isOpen]);
    const {
        session: bulkSession,
        isMenuOpen: isBulkMenuOpen,
        toggleMenu: toggleBulkMenu,
        closeMenu: closeBulkMenu,
    } = useBulkActionsShortcut();

    const shortcutModifier = useMemo(() => (
        /Mac|iPhone|iPad|iPod/i.test(navigator.userAgent) ? 'Cmd' : 'Ctrl'
    ), []);

    const hasBulkShortcut =
        !!bulkSession && bulkSession.selectedCount > 0;

    // Cmd+K / Ctrl+K: bulk actions when selected, else search palette
    const handleKeyboardShortcut = useCallback((e: KeyboardEvent) => {
        if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
            e.preventDefault();
            if (hasBulkShortcut) {
                if (palette.isOpen) palette.onClose();
                toggleBulkMenu();
                return;
            }
            if (isBulkMenuOpen) closeBulkMenu();
            if (palette.isOpen) palette.onClose();
            else palette.onOpen();
        }
    }, [hasBulkShortcut, palette, toggleBulkMenu, isBulkMenuOpen, closeBulkMenu]);

    useEffect(() => {
        document.addEventListener('keydown', handleKeyboardShortcut);
        return () => document.removeEventListener('keydown', handleKeyboardShortcut);
    }, [handleKeyboardShortcut]);

    const label = askCarlAvailable ? 'Search or ask Carl' : 'Search';
    const placeholder = askCarlAvailable ? 'Search or ask Carl…' : 'Search…';

    return (
        <>
            {/* Centered bar — laptop and up. Positioned against the TopBar
                (its nearest positioned ancestor), so it stays centered on the
                page regardless of how wide the breadcrumb or actions are. */}
            <Box
                className="partbay-topbar-search"
                position="absolute"
                top="50%"
                left="50%"
                transform="translate(-50%, -50%)"
                display={{ base: 'none', [TOPBAR_SEARCH_BAR_BREAKPOINT]: 'block' }}
                w={TOPBAR_SEARCH_WIDTH}
                minW="10rem"
                zIndex={6}
            >
                <Button
                    type="button"
                    variant="outlineSubtle"
                    aria-label={label}
                    aria-haspopup="dialog"
                    aria-keyshortcuts="Meta+K Control+K"
                    onClick={palette.onOpen}
                    leftIcon={<Icon name="search" size="md" />}
                    w="100%"
                    minW={0}
                    h="2.5rem"
                    px={3}
                    justifyContent="flex-start"
                    textStyle="body"
                >
                    <Text textStyle="body" color="fg.muted" noOfLines={1} flex="1" textAlign="left">
                        {placeholder}
                    </Text>
                    {askCarlAvailable && (
                        <Icon name="sparkles" size="md" color="brand.fg" flexShrink={0} />
                    )}
                    <HStack spacing={0.5} flexShrink={0} ml={2} aria-hidden>
                        <Kbd textStyle="labelMono" bg="bg.subtle" color="fg.muted" borderColor="border.default" px={1.5} py={0.5}>
                            {shortcutModifier}
                        </Kbd>
                        <Kbd textStyle="labelMono" bg="bg.subtle" color="fg.muted" borderColor="border.default" px={1.5} py={0.5}>
                            K
                        </Kbd>
                    </HStack>
                </Button>
            </Box>

            {/* Compact trigger — below laptop, sits in the right-hand cluster. */}
            <Tooltip label={label} placement="bottom" openDelay={150}>
                <IconButton
                    aria-label={label}
                    icon={<Icon name="search" size="lg" />}
                    variant="ghostGray"
                    display={{ base: 'inline-flex', [TOPBAR_SEARCH_BAR_BREAKPOINT]: 'none' }}
                    onClick={palette.onOpen}
                />
            </Tooltip>

            <CommandSearchModal
                isOpen={palette.isOpen}
                onClose={palette.onClose}
                canAskCarl={askCarlAvailable}
                modifierLabel={shortcutModifier}
                initialQuery={initialQuery}
            />
            <BulkActionsShortcutMenu />
        </>
    );
};

export default SearchBar;
